import { useAccountNotifications, type AppNotification } from './useNotifications';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { isToday, isYesterday, formatDistanceToNow, format } from 'date-fns';
import { sv } from 'date-fns/locale';
import { Bell } from 'lucide-react';

function formatRelative(dateStr: string): string {
  const d = new Date(dateStr);
  if (isToday(d)) return 'Idag';
  if (isYesterday(d)) return 'Igår';
  const diff = Date.now() - d.getTime();
  if (diff < 7 * 24 * 60 * 60 * 1000) {
    return formatDistanceToNow(d, { addSuffix: true, locale: sv });
  }
  return format(d, 'd MMM', { locale: sv });
}

export function NotificationsSection() {
  const { notifications, unreadCount, markAsRead } = useAccountNotifications(2);

  const hasUnread = unreadCount > 0;
  const hasRecent = notifications.length > 0;

  if (!hasUnread && !hasRecent) return null;

  return (
    <section>
      <div className="flex items-center gap-2 mb-3">
        <h2 className="text-sm font-medium text-muted-foreground/70">
          Meddelanden
        </h2>
        {hasUnread && (
          <span className="flex h-2 w-2 rounded-full bg-primary" />
        )}
      </div>

      <Card className="border-border/50 shadow-none">
        <CardContent className="p-0 divide-y divide-border/40">
          {notifications.map((n) => (
            <NotificationRow key={n.id} notification={n} onRead={markAsRead} />
          ))}
        </CardContent>
      </Card>
    </section>
  );
}

function NotificationRow({
  notification,
  onRead,
}: {
  notification: AppNotification;
  onRead: (id: string) => void;
}) {
  const isUnread = !notification.read_at;

  const handleClick = () => {
    if (isUnread) onRead(notification.id);
  };

  const content = (
    <div
      className={`p-3.5 sm:p-4 flex items-start gap-2.5 transition-colors ${
        isUnread ? 'bg-primary/[0.03]' : ''
      }`}
      onClick={handleClick}
    >
      <Bell className={`h-4 w-4 mt-0.5 shrink-0 ${isUnread ? 'text-primary' : 'text-muted-foreground/40'}`} />
      <div className="flex-1 min-w-0">
        <p className={`text-sm leading-snug ${isUnread ? 'font-medium text-foreground' : 'font-medium text-foreground/80'}`}>
          {notification.title}
        </p>
        <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
          {notification.body}
        </p>
        <p className="text-xs text-muted-foreground/50 mt-1">
          {formatRelative(notification.created_at)}
        </p>
      </div>
      {notification.href && (
        <Button variant="ghost" size="sm" className="shrink-0 text-xs text-primary" asChild>
          <span>Visa</span>
        </Button>
      )}
    </div>
  );

  if (notification.href) {
    return (
      <Link to={notification.href} className="block hover:bg-muted/30 transition-colors" onClick={handleClick}>
        {content}
      </Link>
    );
  }

  return content;
}
